/**
 * Admin Agent API — persistent multi-thread chat for the admin agent.
 *
 * Every endpoint is scoped to the calling admin user (resolved from the admin
 * session server-side, never from the body). Threads and their messages belong
 * to exactly one admin; a read/rename/delete of another admin's thread is a 404.
 *
 * The SSE stream endpoint (`POST /api/admin-agent/stream`) is intentionally NOT
 * modelled here — it bypasses the NexusApi envelope like every other assistant
 * stream in the codebase. This domain covers only the thread CRUD surface.
 *
 * Base path: /api/admin-agent/threads
 */

import { z } from "zod";

import { AdminAgentToolCallsSchema } from "../../../schemas/NxAdminAgentMessage";
import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// SHARED DTOs
// ═══════════════════════════════════════════════════════════════

export const AdminAgentMessageRoleSchema = z.enum(["user", "assistant"]);

/** A thread as shown in the sidebar list (no messages). */
export const AdminAgentThreadSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  createdAt: z.string(),
  updatedAt: z.string()
});

/** One persisted message in a thread. `toolCalls` is empty for user turns. */
export const AdminAgentMessageSchema = z.object({
  id: z.string(),
  role: AdminAgentMessageRoleSchema,
  content: z.string(),
  toolCalls: AdminAgentToolCallsSchema,
  createdAt: z.string()
});

// ─── Write confirm gate (Phase 3) ──────────────────────────────

export const AdminAgentProposalActionSchema = z.enum(["write", "delete"]);
export const AdminAgentFrictionSchema = z.enum(["single_key", "type_to_confirm"]);
export const AdminAgentProposalStatusSchema = z.enum(["PENDING", "CONFIRMED", "CANCELLED"]);

/** A best-effort id → human-name pairing surfaced on the confirm card. */
export const AdminAgentResolvedEntitySchema = z.object({
  id: z.string(),
  label: z.string()
});

/**
 * The system-authored, immutable request a proposal would issue. This is the
 * surface `Confirm` commits — the agent cannot author it. Rendered next to the
 * agent's advisory preview, never derived from it.
 */
export const AdminAgentVerifiedRequestSchema = z.object({
  method: z.string(),
  path: z.string(),
  body: z.record(z.string(), z.unknown()).nullable(),
  query: z.record(z.string(), z.string()).nullable(),
  resolvedEntities: z.array(AdminAgentResolvedEntitySchema)
});

/**
 * A pending (or resolved) write proposal as shown to the admin. `agentPreviewHtml`
 * is advisory and sandboxed on render; `verifiedRequest` + `currentState` are the
 * trusted, system-authored surface. `confirmPhrase` is the target's identity —
 * shown always, and the exact string the admin must type when friction is
 * `type_to_confirm`.
 */
export const AdminAgentProposalViewSchema = z.object({
  id: z.string(),
  action: AdminAgentProposalActionSchema,
  friction: AdminAgentFrictionSchema,
  confirmPhrase: z.string(),
  verifiedRequest: AdminAgentVerifiedRequestSchema,
  currentState: z.unknown(),
  agentPreviewHtml: z.string().nullable(),
  status: AdminAgentProposalStatusSchema,
  createdAt: z.string()
});

/** What the confirm endpoint returns: the real admin route's HTTP status + body. */
export const AdminAgentConfirmResultSchema = z.object({
  status: z.number(),
  body: z.unknown()
});

// ═══════════════════════════════════════════════════════════════
// REQUEST SCHEMAS
// ═══════════════════════════════════════════════════════════════

export const CreateAdminAgentThreadBodySchema = z.object({
  /** Optional starting title; defaults to "New chat" when omitted. */
  title: z.string().trim().min(1).max(255).optional()
});

export const RenameAdminAgentThreadBodySchema = z.object({
  title: z.string().trim().min(1).max(255)
});

const ThreadIdPathVars = z.object({ id: z.string() });

const ProposalIdPathVars = z.object({ id: z.string() });

export const ConfirmAdminAgentProposalBodySchema = z.object({
  /** Required and must match the target's identity when friction is `type_to_confirm`. */
  confirmPhrase: z.string().optional()
});

// ═══════════════════════════════════════════════════════════════
// API SCHEMA OBJECT
// ═══════════════════════════════════════════════════════════════

export const ZAdminAgent = {
  /**
   * POST /api/admin-agent/threads
   * Create a new (empty) thread for the calling admin.
   */
  CreateThread: {
    method: "POST" as const,
    path: "/api/admin-agent/threads",
    Body: CreateAdminAgentThreadBodySchema,
    Response: ApiSuccessSchema(AdminAgentThreadSummarySchema)
  },

  /**
   * GET /api/admin-agent/threads
   * List the calling admin's threads, most-recently-active first.
   */
  ListThreads: {
    method: "GET" as const,
    path: "/api/admin-agent/threads",
    Response: ApiSuccessSchema(z.array(AdminAgentThreadSummarySchema))
  },

  /**
   * GET /api/admin-agent/threads/:id/messages
   * List the messages of one thread (oldest first). 404 if not owned.
   */
  GetThreadMessages: {
    method: "GET" as const,
    path: "/api/admin-agent/threads/:id/messages",
    PathVars: ThreadIdPathVars,
    Response: ApiSuccessSchema(z.array(AdminAgentMessageSchema))
  },

  /**
   * PATCH /api/admin-agent/threads/:id
   * Rename a thread. 404 if not owned.
   */
  RenameThread: {
    method: "PATCH" as const,
    path: "/api/admin-agent/threads/:id",
    PathVars: ThreadIdPathVars,
    Body: RenameAdminAgentThreadBodySchema,
    Response: ApiSuccessSchema(AdminAgentThreadSummarySchema)
  },

  /**
   * DELETE /api/admin-agent/threads/:id
   * Delete a thread and its messages (cascade). 404 if not owned.
   */
  DeleteThread: {
    method: "DELETE" as const,
    path: "/api/admin-agent/threads/:id",
    PathVars: ThreadIdPathVars,
    Response: ApiSuccessSchema(z.object({ success: z.boolean() }))
  },

  /**
   * GET /api/admin-agent/threads/:id/proposals
   * The thread's still-PENDING proposals (oldest first), so the UI can rehydrate
   * staged write cards after a reload. 404-equivalent (empty) if not owned.
   */
  ListThreadPendingProposals: {
    method: "GET" as const,
    path: "/api/admin-agent/threads/:id/proposals",
    PathVars: ThreadIdPathVars,
    Response: ApiSuccessSchema(z.array(AdminAgentProposalViewSchema))
  },

  /**
   * POST /api/admin-agent/proposals/:id/confirm
   * Commit a pending write proposal: issues the real admin route as the calling
   * admin (guard + audit apply). Single-use. 404 if not owned/unknown, 409 if
   * already resolved, 400 if the confirm phrase is required and does not match.
   */
  ConfirmProposal: {
    method: "POST" as const,
    path: "/api/admin-agent/proposals/:id/confirm",
    PathVars: ProposalIdPathVars,
    Body: ConfirmAdminAgentProposalBodySchema,
    Response: ApiSuccessSchema(AdminAgentConfirmResultSchema)
  },

  /**
   * POST /api/admin-agent/proposals/:id/cancel
   * Dismiss a pending proposal so it can never be confirmed. 404 if not owned or
   * already resolved.
   */
  CancelProposal: {
    method: "POST" as const,
    path: "/api/admin-agent/proposals/:id/cancel",
    PathVars: ProposalIdPathVars,
    Response: ApiSuccessSchema(z.object({ success: z.boolean() }))
  }
} as const;

// ═══════════════════════════════════════════════════════════════
// EXPORTED TYPES
// ═══════════════════════════════════════════════════════════════

export type AdminAgentThreadSummary = z.infer<typeof AdminAgentThreadSummarySchema>;
export type AdminAgentMessage = z.infer<typeof AdminAgentMessageSchema>;
export type AdminAgentMessageRole = z.infer<typeof AdminAgentMessageRoleSchema>;
export type AdminAgentProposalView = z.infer<typeof AdminAgentProposalViewSchema>;
export type AdminAgentVerifiedRequest = z.infer<typeof AdminAgentVerifiedRequestSchema>;
export type AdminAgentResolvedEntity = z.infer<typeof AdminAgentResolvedEntitySchema>;
export type AdminAgentProposalAction = z.infer<typeof AdminAgentProposalActionSchema>;
export type AdminAgentFriction = z.infer<typeof AdminAgentFrictionSchema>;
export type AdminAgentProposalStatus = z.infer<typeof AdminAgentProposalStatusSchema>;
